<template>
	<CHeaderLayout class="print:hidden" @show-account-menu="$emit('show-account-menu')">
		<template #title>
			<div class="flex items-center gap-3">
				<h1>Plan &amp; Billing</h1>
				<template v-if="showInternalTestingTools">
					<button
						v-if="isTimeTraveling"
						type="button"
						class="organization-settings-plan__time-travel-pill button--no-style"
						title="Time travel active — open simulation controls"
						@click="showStatisticsOverride = true"
					>
						<CBadge type="warning" icon="time" :text="simulatedNowDisplay" />
					</button>
					<CIconButton
						v-else
						icon="time"
						title="Start billing time travel"
						@click="showStatisticsOverride = true"
					/>
					<button
						v-if="quotedExperimentCampaign"
						type="button"
						class="organization-settings-plan__arm-toggle-pill button--no-style"
						:disabled="togglingExperimentArm"
						:title="`Viewing ${quotedArm} pricing. Click to toggle to see ${quotedArm === 'Variant' ? 'Control' : 'Variant'} pricing.`"
						@click="handleToggleExperimentArm"
					>
						<CBadge :type="quotedArm === 'Variant' ? 'alt' : null" icon="flask" :text="`Pricing: ${quotedArm}`" :is-loading="togglingExperimentArm" />
					</button>
				</template>
			</div>
		</template>

		<main class="organization-settings-plan" :class="{'organization-settings-plan--new-billing': isNewBilling}">
			<CSettingsSection
				:loading="loading"
				:loading-size="12"
			>
				<template #button>
					<RouterLink
						v-if="!internalIsHipaaCompliant"
						class="text-2 mb-3 button button--secondary"
						:to="`/${globalStore.organization.Code}/settings/plan/select?source=orgsettings`"
					>
						Change Plan
					</RouterLink>
				</template>

				<CCallout v-if="isOwner && internalIsHipaaCompliant" type="info" title="HIPAA-Compliance is currently enabled." class="mb-4">
					To change your plan, you must exit your Business Associate Agreement located in the Compliance section below.
				</CCallout>
				<CBillingPipelinePlanCards
					v-if="isNewBilling"
					:org-code="globalStore.organization.Code"
					:pricing-tier="pricingTier"
					:current-plan-name="model.EffectivePlanName"
					:current-plan-is-trial="model.IsTrial"
					:current-mode="currentBillingMode"
					:show-buttons="isOwner && !internalIsHipaaCompliant"
					:show-full-comparison-link="!showInlineFeatureComparison"
					:scheduled-change-target-plan-name="model.ScheduledAction?.TargetPlanName"
					:scheduled-change-date="model.ScheduledAction?.ScheduledFor"
					:cancelling-scheduled-change="cancellingDowngrade"
					:quoted-arm="quotedArm"
					:has-prior-paid-plan="hasPriorPaidPlan"
					:quoted-experiment-campaign="quotedExperimentCampaign"
					:current-pricing-source="currentPricingSource"
					source="orgsettings"
					class="mb-5"
					@end-trial="showEndTrialDialog = true"
					@cancel-scheduled-change="showCancelDowngradeDialog = true"
				/>
				<CChangePlanCards
					v-else
					:org-code="globalStore.organization.Code"
					:current-plan-name="model.EffectivePlanName"
					:current-plan-is-trial="model.IsTrial"
					:is-eligible-for-discount="isEligibleForDiscount"
					:show-buttons="isOwner && !internalIsHipaaCompliant"
					class="mb-5"
					@end-trial="showEndTrialDialog = true"
				/>
				<!-- Inline collapsible plan comparison replaces the "See Full Plan Comparison" dialog link (WI 56924) -->
				<CFeatureComparison
					v-if="showInlineFeatureComparison"
					:pricing-tier="featureComparisonPricingTier"
					:baseline-pricing-tier="purchasedPricingTier"
					:manifest="featureComparisonManifest"
					collapsible
					class="my-5"
				/>
				<p class="text-center text-subtle mt-4 mb-5"><small>All prices are shown in USD unless otherwise noted. Sales tax may apply in some jurisdictions.<br>Additional users can be added to the {{ additionalUserPlansText }} plans for ${{ additionalUserFee }}/user/month.</small></p>
				<!-- Current Plan / Trial -->
				<div class="grid b7:grid-cols-2 items-center gap-x-4 b10:gap-x-5 b12:gap-x-6 mb-5">
					<!-- Credit Balance: always for legacy orgs; for new-path non-annual orgs only when credit exists -->
					<div v-if="isOwner && (!useNewBilling || (!model.IsAnnual && model.CurrentBalance !== 0))">
						<h3 class="sz-2 heavy mb-2 flex items-center">
							{{ model.CurrentBalance > 0 ? 'Outstanding Balance' : 'Credit Balance' }}
						</h3>
						<div id="credit-balance">
							{{ Math.abs(model.CurrentBalance || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
							<button v-if="!useNewBilling" class="button button--no-style text-link underline" @click="$router.replace(`/${globalStore.organization.Code}/settings/plan/select?source=orgsettings&details=prepay#prepay`)">Prepay: Buy Credit & Save!</button>
						</div>
					</div>
				</div>

				<!-- Renewal (new-path annual orgs, plus owners with a scheduled downgrade): current-term details always show; next-term Renewal Details are gated -->
				<div v-if="showRenewalSection" class="mb-5">
					<h2 class="font-display text-5 mb-3">Renewal</h2>
					<div class="mb-3">
						<h4 class="sz-2 heavy">Current Plan</h4>
						<p>{{ model.CurrentPlanDisplay }}</p>
					</div>
					<div v-if="model.NextRenewalDate" class="mb-3">
						<h4 class="sz-2 heavy">Renewal Date</h4>
						<p>{{ formatRenewalDate(model.NextRenewalDate) }}</p>
					</div>
					<div v-if="renewalPlanAtDisplay" class="mb-3">
						<h4 class="sz-2 heavy">Plan at Renewal</h4>
						<p>
							{{ renewalPlanAtDisplay }}
							<button v-if="hasScheduledDowngrade" type="button" class="button--no-style text-link underline ml-2" :disabled="cancellingDowngrade" @click="showCancelDowngradeDialog = true">Cancel Downgrade</button>
						</p>
					</div>
					<div v-if="showRenewalSettings && model.RenewalEstimate != null">
						<h4 class="sz-2 heavy">Renewal Estimate <span class="font-normal">(before taxes)</span></h4>
						<p>{{ formatCurrency(model.RenewalEstimate) }}</p>
					</div>
				</div>

				<div v-if="showUsageWalletInfo || hasUsageWalletActivity" class="mb-5 b9:flex b9:items-start b9:gap-6">
					<!-- Usage Wallet (new-path annual orgs — monthly orgs are billed per cycle and have no wallet); balance is owner-only -->
					<div v-if="showUsageWalletInfo" class="b9:flex-1">
						<h2 class="font-display text-5 mb-3">Usage Wallet</h2>
						<p class="mb-3">Prepaid funds for usage that exceeds your plan's included limits. You will be billed monthly for any amount exceeding this credit.</p>
						<CCallout v-if="model.WalletRunningLow" type="warning" size="s" class="mb-3">
							Your wallet is running low. <a class="text-link underline" href="#" @click.prevent="showWalletRefillDialog = true">Add funds</a> now to avoid a monthly bill.
						</CCallout>
						<div class="flex items-center gap-4">
							<span id="usage-wallet-balance">{{ formatCurrency(model.WalletBalance) }}</span>
							<a class="text-link underline" href="#" @click.prevent="showWalletRefillDialog = true">Add funds</a>
						</div>
					</div>
					<!-- Usage Wallet Activity (new-path annual orgs with activity) -->
					<CUsageWalletActivityTable
						v-if="hasUsageWalletActivity"
						:activity="model.UsageWalletActivity"
						:org-code="globalStore.organization.Code"
						class="b9:flex-shrink-0"
						:class="showUsageWalletInfo ? 'mt-4 b9:mt-0' : 'mt-5'"
						@open-usage-details="handleOpenUsageDetails"
					/>
				</div>
				<CWalletRefillDialog
					:visible="showWalletRefillDialog"
					:pricing-tier="pricingTier"
					:current-plan-name="model.EffectivePlanName"
					:current-wallet-balance="model.WalletBalance"
					@close="showWalletRefillDialog = false"
					@refilled="handleRefreshPlan"
				/>
				<PlanStatisticsOverride
					v-if="showInternalTestingTools"
					:visible="showStatisticsOverride"
					@close="showStatisticsOverride = false"
					@billing-advanced="handleRefreshPlan"
				/>

				<OrganizationComplianceSettings v-if="canManageCompliance" :is-hipaa-compliant="internalIsHipaaCompliant" @hipaa-compliant-status-update="handleHipaaStatusChange" />
				<CEndTrialDialog
					v-if="model.IsTrial"
					:visible="showEndTrialDialog"
					:plan-name="model.EffectivePlanName"
					:billing-plan-name="model.BillingPlanName"
					@close="showEndTrialDialog = false"
					@end-trial="handleEndTrial"
				/>
				<CancelScheduledDowngradeDialog
					:visible="showCancelDowngradeDialog"
					:current-plan-name="model.CurrentPlanDisplay"
					:scheduled-downgrade-plan-name="renewalPlanAtDisplay"
					:renewal-date="model.NextRenewalDate"
					@close="showCancelDowngradeDialog = false"
					@cancel-downgrade="handleCancelDowngrade"
				/>

				<!-- Billing Info -->
				<div v-if="isOwner && (editing || model.ShowBillingInfo)" ref="billingInfoHeader" class="flex justify-between text-5 mb-3">
					<h2 class="font-display">Billing Information</h2>
					<a v-show="!editing" title="Edit billing information" class="text-2 button button--secondary" @click="editing = true">Edit</a>
				</div>
				<div>
					<!-- v-show instead of v-if so Stripe mounted fields exist on page load and are not destroyed -->
					<div v-show="editing">
						<ValidationObserver ref="observer">
							<form @submit.prevent="handleFormSubmit" @reset.prevent="resetBillingFields">
								<div class="grid b7:grid-cols-2 items-start gap-x-4 b10:gap-x-5 b12:gap-x-6 mb-5">
									<div class="flex gap-2">
										<CValidatedInput
											vid="first-name"
											:value="newBillingInfo.FirstName || ''"
											label="First Name"
											placeholder="First Name"
											name="First Name"
											type="text"
											rules="required"
											class="w-50"
											@change="handleFieldChange('FirstName', $event)"
										/>
										<CValidatedInput
											vid="last-name"
											:value="newBillingInfo.LastName || ''"
											label="Last Name"
											placeholder="Last Name"
											name="Last Name"
											type="text"
											rules="required"
											class="w-50"
											@change="handleFieldChange('LastName', $event)"
										/>
									</div>
									<CValidatedInput
										vid="email"
										:value="newBillingInfo.EmailAddress || ''"
										label="Email Address"
										placeholder="Email Address"
										name="Email Address"
										type="email"
										rules="required|email"
										@change="handleFieldChange('EmailAddress', $event)"
									/>

									<CValidatedInput
										v-if="!collectTaxId"
										vid="company"
										:value="newBillingInfo.Company || ''"
										label="Company (optional)"
										placeholder="Company"
										name="Company"
										type="text"
										rules=""
										@change="handleFieldChange('Company', $event)"
									/>
									<div class="col-start-1">
										<CAddress
											ref="addressFields"
											:address="newBillingInfo"
											:disable-client-side-validation="isCustomerAddressValidationEnabled"
											@change="handleFieldChange"
										/>
										<fieldset v-if="collectTaxId" id="billing-payment-method">
											<legend class="heavy sz-2 my-3">Payment Method</legend>
											<BillingCardElement
												ref="stripeCard"
												:publishable-key="model.PublishableKey"
												:card-on-file="cardOnFile"
												:existing-card-info="existingCardInfo"
												@change="handleCardChange"
											/>
											<CCallout v-if="cardErrorMessage" type="error" size="s" class="mb-3">
												{{ cardErrorMessage }}
											</CCallout>
										</fieldset>
									</div>

									<CBusinessInformation
										v-if="collectTaxId"
										:organization-type="newBillingInfo.OrganizationType"
										:business="newBillingInfo.Company"
										:tax-ids="newBillingInfo.TaxIds"
										:country-code="newBillingInfo.CountryCode"
										:tax-id-invalid="taxIdInvalid"
										:state="newBillingInfo.State"
										:postal-code="newBillingInfo.PostalCode"
										:purchase-order-number="newBillingInfo.PurchaseOrderNumber"
										:is-v-a-t-registration-opt-out="newBillingInfo.IsVATRegistrationOptOut"
										payment-method="creditcard"
										@reset-tax-id-invalid="taxIdInvalid = false"
										@update:organization-type="handleFieldChange('OrganizationType', $event)"
										@update:business="handleFieldChange('Company', $event)"
										@update:tax-id="handleFieldChange('TaxIds', $event)"
										@update:purchase-order-number="handleFieldChange('PurchaseOrderNumber', $event)"
										@update:is-vat-registration-opt-out="handleFieldChange('IsVATRegistrationOptOut', $event)"
									/>

									<fieldset v-else id="billing-payment-method">
										<legend class="heavy sz-2 my-3">Payment Method</legend>
										<BillingCardElement
											ref="stripeCard"
											:publishable-key="model.PublishableKey"
											:card-on-file="cardOnFile"
											:existing-card-info="existingCardInfo"
											@change="handleCardChange"
										/>
										<CCallout v-if="cardErrorMessage" type="error" size="s" class="mb-3">
											{{ cardErrorMessage }}
										</CCallout>
									</fieldset>
								</div>

								<div class="flex items-center justify-end gap-4 mt-3">
									<button type="reset" class="button--secondary">Cancel</button>
									<button :disabled="executing" type="submit" class="button--primary" :class="{ 'c-button-executing': executing }">{{ showSaveAndPay ? 'Save & Pay' : 'Save' }}</button>
								</div>
							</form>
						</ValidationObserver>
					</div>
					<div v-if="!editing" class="grid b7:grid-cols-2 items-start gap-x-4 b10:gap-x-5 b12:gap-x-6 gap-y-4 mb-4">
						<div v-if="model.BillingInfo.FirstAndLast">
							<h4 class="sz-2 heavy">Name</h4>
							<p>{{ model.BillingInfo.FirstAndLast }}</p>
						</div>
						<div v-if="model.BillingInfo.EmailAddress">
							<h4 class="sz-2 heavy">Email Address</h4>
							<p>{{ model.BillingInfo.EmailAddress }}</p>
						</div>
						<div v-if="model.BillingInfo.Company && !collectTaxId">
							<h4 class="sz-2 heavy">Company</h4>
							<p>{{ model.BillingInfo.Company }}</p>
						</div>
						<div class="flex flex-col gap-x-4 b10:gap-x-5 b12:gap-x-6 gap-y-4">
							<div v-if="model.BillingInfo.Line1">
								<h4 class="sz-2 heavy">Billing Address</h4>
								<p>
									{{ model.BillingInfo.Line1 }}<br>
									{{ model.BillingInfo.Line2 }}<br v-if="model.BillingInfo.Line2">
									{{ model.BillingInfo.CityStatePostalCode }}<br>
									{{ model.BillingInfo.Country }}
								</p>
							</div>
							<div v-if="model.BillingInfo.CreditCardBrand && collectTaxId">
								<h4 class="sz-2 heavy">Payment Method</h4>
								<div class="flex items-center gap-4">
									<p>
										<span class="uppercase">{{ model.BillingInfo.CreditCardBrand }}</span><br>
										{{ 'XXXXXXXXXXXX' + model.BillingInfo.LastFourDigits }}<br>
										{{ model.BillingInfo.ExpirationDateDisplayValue }}
									</p>
									<img v-if="model.BillingInfo.CreditCardBrand == 'discover'" width="48" height="30" class="payment-method-svg mr-2" :src="discoverCardImage" alt="Discover">
									<Inline v-else-if="paymentMethodIconName" :svg="paymentMethodIconName" width="48" height="30" class="payment-method-svg mr-2 text-ma" />
								</div>
							</div>
						</div>
						<div
							v-if="collectTaxId"
							class="flex flex-col gap-x-4 b10:gap-x-5 b12:gap-x-6 gap-y-4"
						>
							<div v-if="model.BillingInfo.OrganizationType">
								<h4 class="sz-2 heavy">Organization Type</h4>
								<p>{{ model.BillingInfo.OrganizationType == 'Business' ? 'Business' : 'Personal' }}</p>
							</div>
							<div v-if="model.BillingInfo.Company">
								<h4 class="sz-2 heavy">Business Name</h4>
								<p>{{ model.BillingInfo.Company }}</p>
							</div>

							<div v-if="displayedTaxIds.length > 0" class="flex flex-col gap-x-4 b10:gap-x-5 b12:gap-x-6 gap-y-4">
								<div v-for="taxIdItem in displayedTaxIds" :key="taxIdItem.type" class="flex items-center gap-4">
									<div class="flex flex-col">
										<h4 class="sz-2 heavy">
											{{ taxIdItem.name }}
										</h4>
										<p>
											{{ taxIdItem.id }}
										</p>
									</div>
									<CToolTip v-if="showTaxIdVerificationBadge(taxIdItem) && isUnverifiedTaxId(taxIdItem)" :use-button="false" placement="top">
										<div class="flex items-start gap-2">
											<Inline svg="warning" class="organization-settings-plan__tax-verification-icon text-icon-warning flex-shrink-0" />
											<span>Your {{ formatTaxIdType(taxIdItem) }} ID is unverified. To avoid paying tax on your next invoice, please update your Tax ID with a valid Tax ID.</span>
										</div>
										<template #button>
											<CBadge :text="taxIdBadgeStatus(taxIdItem)" :type="taxStatusBadgeMap[taxIdBadgeStatus(taxIdItem)]" />
										</template>
									</CToolTip>
									<CBadge v-else-if="showTaxIdVerificationBadge(taxIdItem)" :text="taxIdItem.status" :type="taxStatusBadgeMap[taxIdItem.status]" />
								</div>
							</div>

							<div v-if="model.BillingInfo.PurchaseOrderNumber">
								<h4 class="sz-2 heavy">Purchase Order Number</h4>
								<p>{{ model.BillingInfo.PurchaseOrderNumber }}</p>
							</div>
						</div>
						<div v-else-if="model.BillingInfo.CreditCardBrand">
							<h4 class="sz-2 heavy">Payment Method</h4>
							<div class="flex items-center gap-4">
								<p>
									<span class="uppercase">{{ model.BillingInfo.CreditCardBrand }}</span><br>
									{{ 'XXXXXXXXXXXX' + model.BillingInfo.LastFourDigits }}<br>
									{{ model.BillingInfo.ExpirationDateDisplayValue }}
								</p>
								<img v-if="model.BillingInfo.CreditCardBrand == 'discover'" width="48" height="30" class="payment-method-svg mr-2" :src="discoverCardImage" alt="Discover">
								<Inline v-else-if="paymentMethodIconName" :svg="paymentMethodIconName" width="48" height="30" class="payment-method-svg mr-2 text-ma" />
							</div>
						</div>
					</div>
				</div>

				<!-- Delete Billing Info -->
				<div v-if="model.CanRemoveBillingInfo" class="my-3">
					<button type="button" class="button--secondary" @click="showDeleteBilling = true">
						Remove Payment Method
					</button>
					<CDeleteBillingInfoDialog
						:visible="showDeleteBilling"
						:is-trial="model.IsTrial || false"
						@close="showDeleteBilling = false"
						@delete="removePaymentMethod"
					/>
				</div>

				<CConfirmAddressDialog
					:model="confirmAddressModel"
					@cancel="
						confirmAddressModel = null;
						finishSubmit(false)
					"
					@confirm="handleAddressConfirmation"
				/>

				<COrganizationPlanAndBillingDialog
					:show="showPlanDialog"
					:title="planDialogTitle"
					:max-width="maxDialogWidth"
					:show-confirm="showPlanDialogConfirmation"
					:use-new-billing="useNewBilling"
					:is-hipaa-compliant="internalIsHipaaCompliant"
					@close="showPlanDialog = false"
					@closed="handleClose"
				/>

				<CDialog
					:visible="showUsageDetailsDialog"
					title="Usage Details"
					custom-class="dialog--wide"
					@close="showUsageDetailsDialog = false"
				>
					<OrganizationUsageTable
						:usage-data="usageDetailsData"
						:org-code="globalStore.organization.Code"
						:loading="usageDetailsLoading"
						:show-month-picker="false"
						:show-charged-lines-only="true"
					/>
				</CDialog>

				<!-- Billing History Table -->
				<CBillingTable
					v-if="model.Invoices && model.Invoices.length > 0"
					:invoices="model.Invoices"
					:org-code="globalStore.organization.Code"
					:delinquent-at="delinquentAt"
					:downgrade-at="downgradeAt"
					:simulated-utc-now="model.SimulatedUtcNow"
					:is-annual="model.IsAnnual"
					:is-time-traveling="isTimeTraveling"
					class="mt-5"
					@pay-now="handlePayNow"
				/>

				<div v-if="showDowngradeToIndividual" class="mt-5 flex justify-end">
					<RouterLink
						class="text-link underline"
						:to="{ name: 'organization-settings-subscription-settings-dialog', params: { orgcode: globalStore.organization.Code }, query: { plan: 'Individual' } }"
					>
						Downgrade to Individual
					</RouterLink>
				</div>
			</CSettingsSection>
		</main>
	</CHeaderLayout>
</template>

<script lang="ts">
	import { defineComponent, onMounted, onUnmounted, ref, reactive, computed, nextTick, watch } from 'vue';
	import type { OrganizationPlanBillingSettings, BillingInfo } from '@cognitoforms/api/services/settings/organization-settings-service';
	import { getPlanBillingSettings, reconcileSubscription, trialCancellation, updateBillingInfo, deletePaymentMethod, cancelScheduledDowngrade, getOrganizationUsageByMonthYear } from '@cognitoforms/api/services/settings/organization-settings-service';
	import { FeatureFlags } from '@cognitoforms/types/server-types/feature-flags';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import { ValidationObserver } from 'vee-validate';
	import notify from 'src/utilities/notify';
	import CHeaderLayout from 'src/components/app/HeaderLayout.vue';
	import CChangePlanCards from 'src/components/pricing/ChangePlanCards.vue';
	import CBillingPipelinePlanCards from 'src/components/pricing/BillingPipelinePlanCards.vue';
	import CBillingTable from './BillingTable.vue';
	import CUsageWalletActivityTable from './UsageWalletActivityTable.vue';
	import PlanStatisticsOverride from './PlanStatisticsOverride.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CConfirmAddressDialog from './ConfirmAddressDialog.vue';
	import CDeleteBillingInfoDialog from './DeleteBillingInfoDialog.vue';
	import CEndTrialDialog from './EndTrialDialog.vue';
	import CancelScheduledDowngradeDialog from './CancelScheduledDowngradeDialog.vue';
	import CSettingsSection from './SettingsSection.vue';
	import OrganizationComplianceSettings from './OrganizationComplianceSettings.vue';
	import CValidatedInput from 'src/components/form-controls/ValidatedInput.vue';
	import COrganizationPlanAndBillingDialog from './OrganizationPlanAndBillingDialog.vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import OrganizationUsageTable from './OrganizationUsageTable.vue';
	import session from '@cognitoforms/api/services/session';
	import CAddress from 'src/components/app/Address.vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import BillingCardElement from 'src/components/app/BillingCardElement';
	import { useRoute, useRouter } from 'vue-router/composables';
	import { globalStore } from 'src/stores/global-store';
	import { checkRole } from 'src/views/admin/utils/role-check';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import CBusinessInformation from 'src/views/settings/organization/BusinessInformation.vue';
	import { getTaxIdConfiguration, ValidatedTaxTypes } from 'src/utilities/tax-id/tax-id-configuration';
	import type { BillingAddressConfirmModel } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/billing-address-confirm-model';
	import type { UsageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/usage-view-model';
	import { TaxIdStatus } from '@cognitoforms/types/server-types/plans/model/tax-id-status';
	import { isOpenPayableInvoice } from './billing-invoice-utils';
	import DiscoverCard from '@cognitoforms/shared-components/src/assets/graphics/images/payment-discover.png';
	import CFeatureComparison from 'src/views/pricing/FeatureComparison.vue';
	import CWalletRefillDialog from './checkout/WalletRefillDialog.vue';
	import { getPricing, overridePricingExperimentArmForTesting } from '@cognitoforms/api/services/plan-service';
	import { PricingExperimentArm, writePricingExperimentArm } from '@cognitoforms/utils/pricing-experiment';
	import type { PricingTier } from '@cognitoforms/types/server-types/plans/model/pricing-tier';
	import type { BillingMode } from 'src/components/pricing/billing-mode';
	import type { ManifestJson, SectionManifest } from '@cognitoforms/utils/manifest-helpers';
	import { buildArticleManifest, fetchContentVersion, fetchManifestJson } from '@cognitoforms/utils/manifest-helpers';
	import { startUpgradeStage } from 'src/utilities/journey/activation';
	import { resolveCurrentPlanDimensions } from 'src/utilities/journey/registry';

	interface BillingCardElementRef {
		emptyFields?: {
			cardNumber?: boolean;
			cardExpiry?: boolean;
			cardCvc?: boolean;
		};
		cardNumberElement?: { clear: () => void };
		cardExpiryElement?: { clear: () => void };
		cardCvcElement?: { clear: () => void };
		stripe?: {
			createPaymentMethod: (...args: any[]) => Promise<any>;
			handleCardSetup: (...args: any[]) => Promise<any>;
		};
	}

	export default defineComponent({
		name: 'OrganizationPlanAndBilling',
		components: {
			ValidationObserver,
			CHeaderLayout,
			CChangePlanCards,
			CBillingPipelinePlanCards,
			CFeatureComparison,
			CWalletRefillDialog,
			CBillingTable,
			CUsageWalletActivityTable,
			PlanStatisticsOverride,
			CCallout,
			CConfirmAddressDialog,
			CDeleteBillingInfoDialog,
			CEndTrialDialog,
			CancelScheduledDowngradeDialog,
			CSettingsSection,
			CValidatedInput,
			COrganizationPlanAndBillingDialog,
			CDialog,
			OrganizationUsageTable,
			OrganizationComplianceSettings,
			CAddress,
			CBusinessInformation,
			BillingCardElement,
			CBadge,
			CIconButton,
			CToolTip
		},
		emits: ['component-loaded', 'show-account-menu'],
		setup(_, { emit }) {
			onMounted(() => emit('component-loaded'));

			const isNewBilling = computed(() => session.value?.enabledFeatures.NewBillingPipeline);

			// Pricing + product manifest for the new-billing plan cards and the inline collapsible
			// feature comparison (WI 56924). The page owns the single getPricing() call and feeds
			// both children; manifest loads eagerly because category titles render while collapsed.
			const pricingTier = ref<PricingTier | null>(null);
			const purchasedPricingTier = ref<PricingTier | null>(null);
			const productManifest = ref<SectionManifest | null>(null);
			const quotedArm = ref<string | null>(null);
			const hasPriorPaidPlan = ref(false);
			const quotedExperimentCampaign = ref<string | null>(null);
			const currentPricingSource = ref<string>('');
			const currentPlanIsTrial = ref(false);
			const currentPlanIsBusiness = ref(false);
			const currentRoute = useRoute();
			onMounted(async () => {
				if (!isNewBilling.value)
					return;
				// Both requests go out together, but a failure in either must not take the other down with it:
				// the plan cards stay actionable without the manifest, so the upgrade stage has to open on
				// pricing alone.
				const version = fetchContentVersion().catch(() => null);

				try {
					const pricing = await getPricing();
					pricingTier.value = pricing.currentPricingTier;
					purchasedPricingTier.value = pricing.purchasedPricingTier ?? null;
					quotedArm.value = pricing.quotedArm ?? null;
					hasPriorPaidPlan.value = pricing.isFirstPaidPlan === false;
					quotedExperimentCampaign.value = pricing.quotedExperimentCampaign ?? null;
					currentPricingSource.value = pricing.currentPricingSource ?? '';
					currentPlanIsTrial.value = !!pricing.currentPlanIsTrial;
					currentPlanIsBusiness.value = pricing.currentPlan?.Product?.Name === 'Business';

					if (pricing.isFirstPaidPlan && currentRoute.name === 'organization-settings-plan') {
						startUpgradeStage('orgsettings', undefined, false, resolveCurrentPlanDimensions(pricing.currentPlan?.Name ?? null, null, pricing.quotedArm, pricing.quotedExperimentCampaign));
					}
				}
				catch {
					// The cards fetch their own pricing when pricingTier stays null.
				}

				try {
					const contentVersion = await version;
					if (contentVersion)
						productManifest.value = buildArticleManifest(await fetchManifestJson('product', contentVersion) as ManifestJson);
				}
				catch {
					// The comparison simply doesn't render.
				}
			});

			// Internal-testing-only toggle for the pricing experiment arm: forces the arm cookie to the
			// opposite value and reloads, so a tester can see both the Control and Variant experience
			// on the same org. Gated by showInternalTestingTools, same as billing time travel. A trial
			// org's TrialSignup pricing tier override outranks the cookie, so for trial orgs we also ask
			// the server to force an override onto the requested arm (internal-testing endpoint only) —
			// except on a Business trial, which is only ever quoted variant pricing, so there is no
			// Control experience for the override to show.
			const togglingExperimentArm = ref(false);
			const handleToggleExperimentArm = async () => {
				if (!quotedExperimentCampaign.value || togglingExperimentArm.value)
					return;
				togglingExperimentArm.value = true;
				const nextArm = quotedArm.value === PricingExperimentArm.Variant ? PricingExperimentArm.Control : PricingExperimentArm.Variant;
				writePricingExperimentArm(quotedExperimentCampaign.value, nextArm);
				if (currentPlanIsTrial.value && !currentPlanIsBusiness.value) {
					try {
						await overridePricingExperimentArmForTesting(nextArm);
					}
					catch {
						// Falls back to whatever the trial's existing pricing tier override quotes.
					}
				}
				window.location.reload();
			};
			const collectingInternationalTaxForTaxType = (taxType: string) => {
				if (!session.value?.enabledFeatures.InternationalTax)
					return false;

				const countryCode = taxType.split('_')[0].toUpperCase();
				switch (countryCode) {
				case 'EU':
					return session.value?.enabledFeatures.CollectEUTax ?? false;
				case 'GB':
					return session.value?.enabledFeatures.CollectUKTax ?? false;
				case 'AU':
					return session.value?.enabledFeatures.CollectAUTax ?? false;
				case 'CA':
					return session.value?.enabledFeatures.CollectCATax ?? false;
				default:
					return false;
				};
			};

			const router = useRouter();
			const showPlanDialog = ref(false);
			const showUsageDetailsDialog = ref(false);
			const usageDetailsLoading = ref(false);
			const usageDetailsData = reactive<UsageViewModel>({
				Month: null,
				Items: [],
				FormsUsage: [],
				CurrentBalance: null,
				DashboardNotifications: null,
				OrganizationAge: null
			} as UsageViewModel);
			const loading = ref(true);
			const model = ref<OrganizationPlanBillingSettings>({
				BillingInfo: {
					TaxIds: []
				},
				CurrentPricingTierCode: '',
				UseNewBilling: false,
				IsAnnual: null
			} as unknown as OrganizationPlanBillingSettings);

			const currentBillingMode = computed<BillingMode>(() => model.value.IsAnnual === false ? 'monthly' : 'annual');

			// Internal-only plan-statistics override panel, revealed via Ctrl+Alt+T (non-production, new billing pipeline only).
			const showStatisticsOverride = ref(false);
			const newBillingPipeline = computed(() => !!session.value?.enabledFeatures?.[FeatureFlags.NewBillingPipeline]);

			// Shared gate for every internal-testing-only control on this page (billing time travel, pricing
			// experiment arm toggle): non-production environments running the new billing pipeline.
			const showInternalTestingTools = computed(() => CognitoConfiguration.IsProduction === false && newBillingPipeline.value);

			const handleStatisticsOverrideShortcut = (e: KeyboardEvent) => {
				if (showInternalTestingTools.value && e.ctrlKey && e.altKey && e.code === 'KeyT') {
					e.preventDefault();
					showStatisticsOverride.value = !showStatisticsOverride.value;
				}
			};
			onMounted(() => document.addEventListener('keydown', handleStatisticsOverrideShortcut));
			onUnmounted(() => document.removeEventListener('keydown', handleStatisticsOverrideShortcut));
			const isTimeTraveling = computed(() => !!model.value.SimulatedUtcNow);
			const simulatedNowDisplay = computed(() => {
				const raw = model.value.SimulatedUtcNow;
				if (!raw)
					return '';
				const parsed = new Date(raw as unknown as string);
				if (Number.isNaN(parsed.getTime()))
					return String(raw);
				const date = parsed.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
				const time = parsed.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' });
				return `${date} ${time} UTC`;
			});

			const editing = ref(router.currentRoute.query.edit === 'true');
			const billingInfoHeader = ref<HTMLDivElement | null>(null);
			const confirmAddressModel = ref<BillingAddressConfirmModel | null>(null);
			const newBillingInfo = reactive<BillingInfo>({
				FirstName: '',
				LastName: '',
				EmailAddress: '',
				Company: '',
				Line1: '',
				Line2: '',
				City: '',
				State: '',
				PostalCode: '',
				CountryCode: '',
				Country: '',
				OrganizationType: 'Business',
				IsVATRegistrationOptOut: false,
				TaxIds: [],
				PurchaseOrderNumber: ''
			} as BillingInfo);
			const cardModified = ref(false);
			const cardErrorMessage = ref('');
			const cardOnFile = ref(false);
			const existingCardInfo = {
				cardNumberPlaceholder: '',
				cardExpiryPlaceholder: '',
				cardCvcPlaceholder: ''
			};

			const childRoute = computed(() => {
				return currentRoute.path.split('/')[4] ?? null;
			});

			const stripeCard = ref<BillingCardElementRef>(null);
			const observer = ref<InstanceType<typeof ValidationObserver> | null>(null);

			const handleCardChange = (isCardModified: boolean) => {
				cardModified.value = isCardModified;
				cardErrorMessage.value = '';
			};

			const isOwner = computed(() => checkRole(ClientRole.Owner));
			const handleRefreshPlan = async () =>{
				if (isOwner.value) {
					loading.value = true;
					model.value = {
						...model.value,
						...(await reconcileSubscription())
					};
					Object.assign(newBillingInfo, model.value.BillingInfo);
					loading.value = false;

					if (model.value.BillingInfo.CreditCardReferenceId?.trim()) {
						cardOnFile.value = true;
						existingCardInfo.cardNumberPlaceholder = model.value.BillingInfo.CreditCardInfo;
						existingCardInfo.cardExpiryPlaceholder = model.value.BillingInfo.ExpirationDateDisplayValue;
						existingCardInfo.cardCvcPlaceholder = '***';
					}

					if (model.value.ShowBillingInfo && router.currentRoute.query.edit === 'true') {
						setTimeout(() => {
							billingInfoHeader.value?.scrollIntoView({ behavior: 'smooth' });
							observer.value?.validate();
						}, 1000);
					}
					else if (router.currentRoute.hash === '#invoices') {
						setTimeout(() => {
							document.getElementById('invoices')?.scrollIntoView({ behavior: 'smooth' });
						}, 1000);
					}
				}
				else {
					model.value.EffectivePlanName = globalStore.organization.Plan.Name;
					model.value.IsTrial = false;
					loading.value = false;
				}
			};

			const internalIsHipaaCompliant = ref(globalStore.organization?.IsHipaaCompliant);

			const handleHipaaStatusChange = (status: boolean, refresh: boolean) => {
				internalIsHipaaCompliant.value = status;
				if (refresh)
					handleRefreshPlan();
			};

			const isEligibleForDiscount = computed(() => {
				const code = model.value.CurrentPricingTierCode ?? '';
				return typeof code === 'string' && code.startsWith('DISCOUNT');
			});

			// New-path gating: the NewBillingPipeline flag (rollout) AND the org's own new-billing
			// state (BillingMode != Legacy, surfaced as UseNewBilling). Both are required so a
			// flag-on-but-still-Legacy org doesn't see empty Renewal/Wallet sections.
			const useNewBilling = computed(() => !!session.value?.enabledFeatures?.[FeatureFlags.NewBillingPipeline]);
			const showNewBilling = computed(() => useNewBilling.value && model.value.UseNewBilling);
			const hasScheduledDowngrade = computed(() => showNewBilling.value && isOwner.value && !!model.value.ScheduledAction);

			// Next-term Renewal Details: annual-only, and only once the monthly run has created the
			// renewal invoice (org is nearing renewal). The renewal date itself always shows for annual orgs.
			const showRenewalSettings = computed(() => showNewBilling.value && model.value.IsAnnual && model.value.HasPendingRenewalInvoice);
			const showInlineFeatureComparison = computed(() => isNewBilling.value && !!pricingTier.value && !!productManifest.value && (model.value.EffectivePlanName === 'Individual' || model.value.IsTrial));
			const featureComparisonPricingTier = computed(() => pricingTier.value as PricingTier);
			const featureComparisonManifest = computed(() => productManifest.value as SectionManifest);
			const showRenewalSection = computed(() => showNewBilling.value && (model.value.IsAnnual || hasScheduledDowngrade.value));
			const renewalPlanAtDisplay = computed(() => model.value.ScheduledAction?.TargetPlanName || '');
			const showDowngradeToIndividual = computed(() =>
				useNewBilling.value
				&& model.value.CanManageBilling
				&& model.value.EffectivePlanName !== 'Individual'
				&& model.value.BillingPlanName !== 'Individual'
				&& !model.value.ScheduledAction?.TargetPlanName?.startsWith('Individual'));
			const showUsageWalletInfo = computed(() => isOwner.value && showNewBilling.value && model.value.IsAnnual);
			const hasUsageWalletActivity = computed(() => !!(model.value.UsageWalletActivity && model.value.UsageWalletActivity.length > 0));

			const formatCurrency = (amount: number) => (amount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
			const formatRenewalDate = (date: Date | string) => new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC' });

			const isDelinquent = computed(() => {
				return !!model.value?.DelinquentAt;
			});

			const hasOpenPayableInvoice = computed(() => {
				const invoices = model.value?.Invoices ?? [];
				return invoices.some(invoice => isOpenPayableInvoice(invoice, model.value.IsAnnual));
			});

			const showSaveAndPay = computed(() => useNewBilling.value && (isDelinquent.value || hasOpenPayableInvoice.value));

			const delinquentAt = computed(() => {
				return model.value?.DelinquentAt ?? null;
			});

			const downgradeAt = computed(() => {
				return model.value?.DowngradeAt ?? null;
			});

			watch(() => currentRoute.name, async (newName, former) => {
					showPlanDialog.value = false;

					// Inside change plan flow
					if (newName !== 'organization-settings-plan' && !showPlanDialog.value) {
						const plan = currentRoute.query.plan?.toString().toLowerCase();
						if (childRoute.value === 'subscription' &&
							(!isOwner.value || (globalStore.organization?.IsHipaaCompliant && plan && plan !== 'enterprise'))) {
							// an admin may be directed to the subscription page, but they should only be able to see the the plans setting page
							// a hipaa owner needs to be able to prepay for their plan (which would have the child route 'subscription') but only if the plan is enterprise
							router.replace(`/${globalStore.organization.Code}/settings/plan`);
						}
						else {
							nextTick(()=> {
								showPlanDialog.value = true;
							});
						}
					}
					else if (['organization-settings-change-plan-confirmation-dialog', 'organization-settings-prepay-check-confirmation-dialog'].includes(former))
						handleRefreshPlan();
					else if (['organization-settings-select-plan-dialog', 'organization-settings-subscription-settings-dialog', 'organization-settings-checkout-plan-dialog'].includes(former) && useNewBilling.value)
						handleRefreshPlan();
				},
				{ immediate: true }
			);

			watch(() => globalStore.organization?.IsHipaaCompliant, () => {
				internalIsHipaaCompliant.value = globalStore.organization?.IsHipaaCompliant;
			});

			const maxDialogWidth = computed(()=>{
				if (childRoute.value === 'prepay-check-confirmation')
					return '700px';
				return '1200px';
			});

			const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

			const planDialogTitle = computed(() => {
				if (childRoute.value === 'select')
					return 'Plan Comparison';
				else if (childRoute.value === 'subscription') {
					const plan = currentRoute.query['plan'] as string;
					const allowedPlans = ['individual', 'pro', 'team', 'enterprise'];
					if (plan && allowedPlans.includes(plan.toLowerCase())) {
						if (useNewBilling.value && plan.toLowerCase() === 'individual')
							return 'Schedule Individual Plan Downgrade';
						else
							return `${ currentRoute.query['prepaymonths'] && currentRoute.query['prepaymonths'] !== '0' ? 'Prepay for' : 'Change Your Plan to' } ` + capitalize(plan);
					}
					else
						return '';
				}
				else if (childRoute.value === 'confirmation')
					return 'Thank You!';
				return '';
			});

			const displayedTaxIds = computed(() => {
				const billingInfo = model.value.BillingInfo;
				if (!billingInfo?.TaxIds?.length) return [];

				const taxOptions = getTaxIdConfiguration(billingInfo.CountryCode, billingInfo.State, billingInfo.PostalCode);
				const taxIdMap = billingInfo.TaxIds.reduce((map, taxId) => {
					map[taxId.Type] = taxId;
					return map;
				}, {});

				return taxOptions
					.map(taxOption => ({
						name: taxOption.name,
						type: taxOption.type,
						id: taxIdMap[taxOption.type]?.Id || null,
						status: taxIdMap[taxOption.type]?.Status
					}))
					.filter(item => item.id);
			});

			const taxStatusBadgeMap = {
				'Verified': 'success',
				'Pending': null, // default badge is gray
				'Unverified': 'error'
			};

			const formatTaxIdType = (taxIdItem) => {
				const type = taxIdItem.type;
				if (!type || !type.includes('_'))
					return 'VAT';
				else if (type === 'au_abn')
					return 'GST';
				const withoutCountryCode = type.substring(type.indexOf('_') + 1);
				return withoutCountryCode.replace(/_/g, '/').toUpperCase();
			};

			const showTaxIdVerificationBadge = (taxIdItem) => {
				return [TaxIdStatus.Pending, TaxIdStatus.Verified, TaxIdStatus.Unverified, TaxIdStatus.Unknown].includes(taxIdItem.status) && ValidatedTaxTypes.includes(taxIdItem.type) && collectingInternationalTaxForTaxType(taxIdItem.type) && !newBillingInfo.IsVATRegistrationOptOut;
			};

			const isUnverifiedTaxId = (taxIdItem) => taxIdItem.status === TaxIdStatus.Unverified || taxIdItem.status === TaxIdStatus.Unknown;

			const taxIdBadgeStatus = (taxIdItem) => isUnverifiedTaxId(taxIdItem) ? TaxIdStatus.Unverified : taxIdItem.status;

			const additionalUserFee = computed(() => pricingTier.value?.Pro?.AdditionalUserFee ?? 5);
			const additionalUserPlansText = computed(() => pricingTier.value?.Business ? 'Pro, Team, Business, and Enterprise' : 'Pro, Team, and Enterprise');
			const cancellingDowngrade = ref(false);
			const showCancelDowngradeDialog = ref(false);
			const handleCancelDowngrade = async () => {
				cancellingDowngrade.value = true;
				try {
					model.value = await cancelScheduledDowngrade();
				}
				catch {
					notify('Failed to cancel the scheduled downgrade.', 'error');
				}
				finally {
					showCancelDowngradeDialog.value = false;
					cancellingDowngrade.value = false;
				}
			};

			return {
				globalStore: globalStore,
				model,
				showStatisticsOverride,
				showInternalTestingTools,
				isTimeTraveling,
				simulatedNowDisplay,
				togglingExperimentArm,
				handleToggleExperimentArm,
				confirmAddressModel,
				newBillingInfo,
				loading,
				isOwner,
				executing: ref(false),
				editing,
				billingInfoHeader,
				showEndTrialDialog: ref(false),
				showWalletRefillDialog: ref(false),
				showDeleteBilling: ref(false),
				cardModified,
				cardErrorMessage,
				cardOnFile,
				existingCardInfo,
				showPlanDialog,
				showUsageDetailsDialog,
				usageDetailsLoading,
				usageDetailsData,
				planDialogTitle,
				showPlanDialogConfirmation: computed(() => childRoute.value === 'subscription'),
				maxDialogWidth,
				isNewBilling,
				handleRefreshPlan,
				handleCardChange,
				canManageCompliance: computed(()=> checkRole(ClientRole.Owner)),
				internalIsHipaaCompliant,
				handleHipaaStatusChange,
				taxIdInvalid: ref(false),
				collectTaxId: computed(() => session.value?.enabledFeatures.CollectTaxId),
				isCustomerAddressValidationEnabled: computed(() => session.value?.enabledFeatures?.CustomerAddressValidation),
				displayedTaxIds,
				stripeCard,
				observer,
				isEligibleForDiscount,
				pricingTier,
				purchasedPricingTier,
				productManifest,
				quotedArm,
				hasPriorPaidPlan,
				quotedExperimentCampaign,
				currentPricingSource,
				featureComparisonPricingTier,
				featureComparisonManifest,
				currentBillingMode,
				showNewBilling,
				showInlineFeatureComparison,
				isDelinquent,
				showSaveAndPay,
				hasScheduledDowngrade,
				showRenewalSection,
				showRenewalSettings,
				renewalPlanAtDisplay,
				showDowngradeToIndividual,
				cancellingDowngrade,
				showCancelDowngradeDialog,
				handleCancelDowngrade,
				showUsageWalletInfo,
				hasUsageWalletActivity,
				formatCurrency,
				formatRenewalDate,
				delinquentAt,
				downgradeAt,
				useNewBilling,
				taxStatusBadgeMap,
				formatTaxIdType,
				showTaxIdVerificationBadge,
				isUnverifiedTaxId,
				taxIdBadgeStatus,
				additionalUserFee,
				additionalUserPlansText
			};
		},
		head() {
			return {
				title: 'Plan & Billing - Cognito Forms',
				titleTemplate: '%s'
			};
		},
		computed: {
			discoverCardImage() {
				return DiscoverCard;
			},
			paymentMethodIconName() {
				// Note: Discover is currently loaded as a PNG due to ids being converted to classes for SVGs
				const cardType = this.model.BillingInfo.CreditCardBrand.toLowerCase();
				switch (cardType) {
				case 'visa':
				case 'mastercard':
				case 'americanexpress':
				case 'paypal':
				case 'venmo':
					return `images/payment-${cardType}`;
				case 'none':
				case 'manual':
					return 'images/payment-cash';
				default:
					return 'images/payment-card';
				}
			}
		},
		async mounted() {
			this.handleRefreshPlan();
		},
		methods: {
			handlePayNow() {
				this.editing = true;
				this.$nextTick(() => {
					this.billingInfoHeader?.scrollIntoView({ behavior: 'smooth' });
				});
			},
			handleFieldChange(field: string, value: string) {
				this.newBillingInfo[field] = value;
			},
			handleClose() {
				this.showPlanDialog = false;
				this.$router.replace(`/${this.globalStore.organization.Code}/settings/plan`);
			},
			async handleOpenUsageDetails(month: Date | string) {
				this.showUsageDetailsDialog = true;
				this.usageDetailsLoading = true;

				try {
					const monthDate = new Date(month);
					const year = monthDate.getUTCFullYear();
					const monthNumber = monthDate.getUTCMonth() + 1;
					Object.assign(this.usageDetailsData, await getOrganizationUsageByMonthYear(year, monthNumber));
				}
				finally {
					this.usageDetailsLoading = false;
				}
			},
			async refreshSettings() {
				this.model = Object.assign({}, this.model, await getPlanBillingSettings());
				await this.$nextTick();
			},
			async handleEndTrial() {
				await trialCancellation();
				this.showEndTrialDialog = false;
				this.refreshSettings();
			},
			async removePaymentMethod() {
				try {
					await deletePaymentMethod();
					notify('Your organization payment method has been deleted.', 'success');
					await this.refreshSettings();
					this.cardOnFile = false;
					this.existingCardInfo.cardNumberPlaceholder = this.model.BillingInfo.CreditCardInfo;
					this.existingCardInfo.cardExpiryPlaceholder = this.model.BillingInfo.ExpirationDateDisplayValue;
					this.existingCardInfo.cardCvcPlaceholder = '***';
					this.resetBillingFields();
				}
				catch {
					notify('Failed to delete payment method.', 'error');
				}
				this.showDeleteBilling = false;
			},
			async resetBillingFields() {
				this.cardErrorMessage = '';
				Object.assign(this.newBillingInfo, this.model.BillingInfo);
				const stripeElement = this.stripeCard as BillingCardElementRef;
				if (stripeElement && stripeElement.cardNumberElement) {
					stripeElement.cardNumberElement.clear();
					stripeElement.cardExpiryElement.clear();
					stripeElement.cardCvcElement.clear();
				}
				this.cardModified = false;
				this.observer.reset();
				this.editing = false;
			},
			async handleFormSubmit() {
				const isVeeValidateValid = await this.observer.validate();

				// If validation fails, don't submit
				if (!isVeeValidateValid) {
					return;
				}

				// Validation, proceed with submission
				this.submitBillingInfo();
			},
			async handleAddressConfirmation(address) {
				this.newBillingInfo.Line1 = address.Line1;
				this.newBillingInfo.Line2 = address.Line2;
				this.newBillingInfo.City = address.City;
				this.newBillingInfo.State = address.State;
				this.newBillingInfo.PostalCode = address.PostalCode;
				this.newBillingInfo.Country = address.Country;
				this.newBillingInfo.CountryCode = address.CountryCode;

				// Run both validations before submitting from address confirmation
				const isVeeValidateValid = await this.observer.validate();

				if (isVeeValidateValid) {
					this.submitBillingInfo(false).then(() => {
						this.confirmAddressModel = null;
					});
				}
				else {
					this.confirmAddressModel = null;
					this.finishSubmit(false);
				}
			},
			finishSubmit(success: boolean) {
				if (success) {
					notify('Your organization billing information has been updated.', 'success');
					this.editing = false;
					this.observer.reset();
				}
				else {
					notify('Failed to update billing information.', 'error');
				}
				this.executing = false;
			},
			async submitBillingInfo(shouldValidateAddress = true) {
				this.executing = true;
				this.cardErrorMessage = '';
				let updateBillingSuccess = false;
				const wasPastDue = !!(this.model.DelinquentAt || this.model.DowngradeAt);

				// If card data modified, submit updated billing info to Stripe
				if (this.cardModified) {
					try {
						const stripeResponse = await this.stripeCard.stripe.createPaymentMethod('card', this.stripeCard.cardNumberElement, {
							billing_details: {
								name: `${this.newBillingInfo.FirstName} ${this.newBillingInfo.LastName}`,
								address: {
									line1: this.newBillingInfo.Line1,
									line2: this.newBillingInfo.Line2,
									city: this.newBillingInfo.City,
									state: this.newBillingInfo.State,
									postal_code: this.newBillingInfo.PostalCode
								}
							}
						});
						if (!stripeResponse.error) {
							this.newBillingInfo.AuthorizationTokenText = stripeResponse.paymentMethod.id;
							this.newBillingInfo.AuthorizationTokenType = 'PaymentMethod';
							this.newBillingInfo.AuthorizationTokenCreateSetupIntent = window.location.search.includes('disableUseSetupIntent') ? 'False' : 'True';
							this.newBillingInfo.ExpirationDate = `${stripeResponse.paymentMethod.card.exp_month}/${stripeResponse.paymentMethod.card.exp_year.toString()}`;
							await this.updateCard(shouldValidateAddress);
							return;
						}
						else {
							this.cardErrorMessage = stripeResponse.error.message;
						}
					}
					catch (e) {
						// Exception when attempting to send card to Stripe
						this.cardErrorMessage = e ? (e.message || e.toString()) : 'Unknown error';
					}
				}
				// No card data modified, submit updated billing info directly to Cognito
				else {
					try {
						if (this.newBillingInfo.OrganizationType === 'Individual') {
							this.newBillingInfo.Company = '';
							this.newBillingInfo.TaxIds = [];
							this.newBillingInfo.IsVATRegistrationOptOut = false;
						}

						// Clear TaxIds if VAT registration opt-out is selected (except those with optionalWhenOptedOut)
						if (this.newBillingInfo.IsVATRegistrationOptOut) {
							const taxOptions = getTaxIdConfiguration(this.newBillingInfo.CountryCode, this.newBillingInfo.State, this.newBillingInfo.PostalCode);
							const optionalWhenOptedOutTypes = taxOptions
								.filter(opt => opt.optionalWhenOptedOut)
								.map(opt => opt.type);

							this.newBillingInfo.TaxIds = this.newBillingInfo.TaxIds.filter(taxId =>
								optionalWhenOptedOutTypes.includes(taxId.Type)
							);
						}

						const response = await updateBillingInfo(this.newBillingInfo, this.model.SubmissionToken, shouldValidateAddress);
						if ('Type' in response) {
							switch (response.Type) {
							case 'SetupPaymentMethodError':
								if (response.TaxIdInvalid)
									this.taxIdInvalid = true;
								else
									this.cardErrorMessage = response.Error;
								break;
							case 'BillingAddressConfirm':
								this.confirmAddressModel = response;
								// don't finalize submission, wait for dialog to complete
								return;
							case 'BillingAddressError':
								this.observer.setErrors(response.Errors);
								break;
							}
						}
						else {
							updateBillingSuccess = true;
							this.model = {
								...this.model,
								...response
							};
							if (wasPastDue) {
								this.model.DelinquentAt = null;
								this.model.DowngradeAt = null;
							}
						}
					}
					catch (e) {
						// Exception when attempting to submit billing info to Cognito
						this.cardErrorMessage = e ? (e.message || e.toString()) : 'Unknown error';
					}
				}
				this.finishSubmit(updateBillingSuccess);
			},
			async updateCard(shouldValidateAddress = true) {
				// Stripe payment method created, update card on file
				let updateBillingSuccess = false;
				const wasPastDue = !!(this.model.DelinquentAt || this.model.DowngradeAt);
				try {
					if (this.newBillingInfo.OrganizationType === 'Individual') {
						this.newBillingInfo.Company = '';
						this.newBillingInfo.TaxIds = [];
						this.newBillingInfo.IsVATRegistrationOptOut = false;
					}

					// Clear TaxIds if VAT registration opt-out is selected (except those with optionalWhenOptedOut)
					if (this.newBillingInfo.IsVATRegistrationOptOut) {
						const taxOptions = getTaxIdConfiguration(this.newBillingInfo.CountryCode, this.newBillingInfo.State, this.newBillingInfo.PostalCode);
						const optionalWhenOptedOutTypes = taxOptions
							.filter(opt => opt.optionalWhenOptedOut)
							.map(opt => opt.type);

						this.newBillingInfo.TaxIds = this.newBillingInfo.TaxIds.filter(taxId =>
							optionalWhenOptedOutTypes.includes(taxId.Type)
						);
					}

					const response = await updateBillingInfo(this.newBillingInfo, this.model.SubmissionToken, shouldValidateAddress);
					if (!('Type' in response)) {
						updateBillingSuccess = true;
						this.model = {
							...this.model,
							...response
						};
						if (wasPastDue) {
							this.model.DelinquentAt = null;
							this.model.DowngradeAt = null;
						}

						const stripeElement = this.stripeCard;
						if (stripeElement && stripeElement.cardNumberElement) {
							stripeElement.cardNumberElement.clear();
							stripeElement.cardExpiryElement.clear();
							stripeElement.cardCvcElement.clear();
						}
						this.cardModified = false;
						if (this.model.BillingInfo.CreditCardReferenceId?.trim()) {
							this.cardOnFile = true;
							this.existingCardInfo.cardNumberPlaceholder = this.model.BillingInfo.CreditCardInfo;
							this.existingCardInfo.cardExpiryPlaceholder = this.model.BillingInfo.ExpirationDateDisplayValue;
							this.existingCardInfo.cardCvcPlaceholder = '***';
						}
					}
					else {
						switch (response.Type) {
						case 'SetupPaymentMethodError':
							// Card requires authentication, prompt authentication through Stripe
							if (response.SetupIntentId && response.ClientSecret) {
								this.newBillingInfo.AuthorizationTokenText = response.SetupIntentId;
								this.newBillingInfo.AuthorizationTokenType = 'SetupIntent';
								this.newBillingInfo.AuthorizationTokenClientSecret = response.ClientSecret;
								try {
									const actionResult = await this.stripeCard.stripe.handleCardSetup(response.ClientSecret);
									if (!actionResult.error) {
										// Hide the error message now that the user has successfully authenticated
										this.cardErrorMessage = '';
										this.newBillingInfo.AuthorizationTokenCreateSetupIntent = 'False';
										this.updateCard();
										return;
									}
									else {
										this.cardErrorMessage = 'Card failed authentication.';
									}
								}
								catch (e) {
									// Exception when attempting to prompt user for authentication using Stripe
									this.cardErrorMessage = e ? (e.message || e.toString()) : 'Unknown error';
								}
							}
							else if (response.TaxIdInvalid) {
								this.taxIdInvalid = true;
							}
							// If there is an error other than requiring authentication, set the error message
							else {
								this.cardErrorMessage = response.Error;
							}
							break;
						case 'BillingAddressConfirm':
							this.confirmAddressModel = response;
							// don't finalize submission, wait for dialog to complete
							return;
						case 'BillingAddressError':
							this.observer.setErrors(response.Errors);
							break;
						}
					}
				}
				catch (e) {
					// Exception when attempting to submit billing info to Cognito
					this.cardErrorMessage = e ? (e.message || e.toString()) : 'Unknown error';
				}
				this.finishSubmit(updateBillingSuccess);
			}
		}
	});
</script>

<style lang="scss">
.print\:hidden {
	@media print {
		display: none !important;
	}
}

.organization-settings-plan {
	--content-max-width: 896px;

	.admin--mobile.admin--app-nav & {
		padding-top: $space__3;
	}

	.settings__loader-content {
		$half: calc(50% - $space__4);

		div {
			// Cards
			&:nth-child(1),
			&:nth-child(2),
			&:nth-child(3),
			&:nth-child(4) {
				width: calc((100% - ($space__5 * 3)) / 4);
				height: 238px;
			}

			&:nth-child(5),
			&:nth-child(8),
			&:nth-child(11) {
				width: $half;
				height: 40px;
				margin-right: $half;
			}
		}
	}

	&--new-billing {

		.settings__loader-content {
			container: new-billing-loader-content / inline-size;

			div {

				&:nth-child(1) {
					width: 288px;
					height: 58px;
					margin: 0 calc(50% - 288px / 2);
				}

				&:nth-child(2),
				&:nth-child(3),
				&:nth-child(4) {
					width: 100%;
					height: 549px;
				}

				@container new-billing-loader-content (min-width: 601px) {

					&:nth-child(2),
					&:nth-child(3),
					&:nth-child(4) {
						width: calc((100% - ($space__5 * 2)) / 3);
						height: 549px;
					}
				}
			}
		}

		.billing-field-container {
			position: relative;
			height: 29.594px;
			background: var(--bg-input);

			.StripeElement--focus {

				&::before {
					position: absolute;
					bottom: 0;
					left: 0;
					right: 0;
					top: 0;
					outline: 1px solid var(--outline-focused);
					content: '';
				}
			}
		}

		input:focus {
			outline: 1px solid var(--outline-focused);
		}

		.input[name='Line 1'] {
			padding-bottom: $space__2;
		}

		// Safari needs explicit width/height
		.payment-method-svg {
			width: 48px;
			height: 30px;
		}

		&__tax-verification-icon {
			margin-top: 1px;
		}

		&__time-travel-pill,
		&__arm-toggle-pill {
			cursor: pointer;
			line-height: 0;
			transition: opacity 150ms ease;

			&:hover,
			&:focus-visible {
				opacity: .8;
			}
		}
	}
}
</style>